// Hand-typed mirrors of the backend API contract.
// Replace with `openapi-typescript` generation once we lock the spec; for v1
// the surface is small enough that drift is easy to spot.

// ---------- /profile ----------

// ---------- /availability-patterns ----------

/**
 * One availability window on an availability pattern.
 *   earliest / latest — "HH:mm" local times (the <input type="time"> value)
 *   days              — lowercase weekday names (e.g. ["monday","tuesday"])
 *
 * A type may own several. Two on the same weekday express a lunch break, so
 * they must not overlap — the server rejects overlapping ranges rather than
 * silently merging them.
 */
export interface AvailabilityRangeInput {
  days: string[];
  earliest: string;
  latest: string;
}

/**
 * One of the caller's own availability patterns: a name they chose, plus the
 * availability it offers when they invite someone with it. Exactly one type per
 * user is the default — what an incoming invitation is checked against, and what
 * a single-type user invites with silently.
 */
export interface AvailabilityPatternView {
  id: string;
  name: string;
  is_default: boolean;
  ranges: AvailabilityRangeInput[];
  /**
   * The rolling link that owns this pattern, or absent when the host wrote it themselves.
   *
   * <p>Shown so the two can be walked between, and read-only: the link rewrites its pattern on
   * every save, so an edit here would be silently undone.</p>
   */
  owned_by_link?: { id: string; handle: string; title: string } | null;
}

/** Body for POST / PATCH /api/v1/availability-patterns. Null fields are left unchanged. */
export interface AvailabilityPatternUpsertRequest {
  name?: string;
  ranges?: AvailabilityRangeInput[];
}

/**
 * GET /api/v1/profile response. Note that ProfileView on the backend is a
 * bare Java record with no @JsonProperty annotations, so the wire shape is
 * camelCase (unlike the rest of the v1 API which is mostly snake_case).
 * Keep this in sync if the backend ever adopts a global Jackson naming
 * strategy.
 *
 * <p>SPA uses {@link ProfileView.handle} as the gate signal: when it's
 * null, the user must complete /profile/setup before the rest of the app
 * is reachable.</p>
 *
 * <p>Availability patterns are no longer part of the profile: they are the user's own
 * outbound templates and live at /api/v1/availability-patterns.</p>
 */
export interface ProfileView {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  handle: string | null;
  /** IANA timezone the user's availability windows are interpreted in. */
  timezone: string | null;
  /** "You could interest me in …" lines shown on the agent card. */
  cardInterests: string[];
  /** Optional social profiles, in display order. Canonicalised by the server. */
  socialLinks: SocialLink[];
  /** Uploaded profile picture, or null. */
  avatarUrl: string | null;
  /** Uploaded branding banner, or null. */
  bannerUrl: string | null;
  /** The kept original behind avatarUrl. Present only when there is one to reframe. */
  avatarSourceUrl: string | null;
  /** The kept original behind bannerUrl. Present only when there is one to reframe. */
  bannerSourceUrl: string | null;
  /** Who can see the agent card. */
  cardVisibility: CardVisibility;
  /** How auto-booked meetings reach this user: onto their calendar, or by email. */
  bookingDelivery: BookingDelivery;
  tier: Tier;
  /**
   * Whether people may reach this host through an agent without holding one of their links.
   *
   * <p>The account-level half of reachability. Each rolling link carries its own switch and both
   * must be on, so this being true does not by itself expose anything.</p>
   */
  a2aHostEnabled: boolean;
  /**
   * True when the handle predates the letters+digits rule (contains a
   * hyphen) — the profile page offers a one-time rename.
   */
  handleNeedsMigration: boolean;
  /** True when the user is on the admin_user allowlist — gates /admin. */
  admin: boolean;
  /**
   * True when the user may use the app (APPROVED, or an admin). When false the
   * SPA shows the waitlist screen instead of the app.
   */
  approved: boolean;
  /** Raw approval state, for waitlist messaging (pending vs disabled). */
  approvalStatus: ApprovalStatus;
  /**
   * Version of the Terms of Service this user must have accepted, e.g. "2026-08-12".
   *
   * Sent down by the server rather than hardcoded here on purpose: `LegalDocuments` in the backend
   * is the single source of truth, so there is no constant to drift out of step. The gate posts
   * this exact value back, and the server rejects it if it no longer matches.
   */
  termsVersionRequired: string;
  /**
   * Whether the user has accepted `termsVersionRequired`. False for every account that has not,
   * which is what raises the gate, and false again for everyone the moment the version is bumped.
   */
  termsAccepted: boolean;
}

/** Admin-approval state of a user. */
export type ApprovalStatus = "pending" | "approved" | "disabled";

/**
 * One row of GET /api/v1/admin/users (admin-only). {@code handle} is null
 * for users who signed up but never completed /profile/setup — render those
 * without a card link. camelCase wire shape, same as ProfileView.
 */
export interface AdminUserView {
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  handle: string | null;
  timezone: string | null;
  cardVisibility: CardVisibility;
  createdAt: string;
  admin: boolean;
  /** Approval state — drives the status badge and enable/disable action. */
  approvalStatus: ApprovalStatus;
  /** How many preview-interest notes this user has sent. */
  interestCount: number;
  /** Their most recent note, or null. */
  latestInterest: string | null;
  /** When the most recent note was sent (ISO), or null. */
  latestInterestAt: string | null;
  /** LLM tokens used today / this month (UTC). */
  tokensToday: number;
  tokensMonth: number;
  /** Admin-set caps; null = unlimited. */
  dailyTokenLimit: number | null;
  monthlyTokenLimit: number | null;
  /** Subscription tier. */
  tier: Tier;
}

/** The signed-in user's own token consumption + caps (GET /token-usage). */
export interface TokenUsageView {
  usedToday: number;
  usedMonth: number;
  dailyLimit: number | null;
  monthlyLimit: number | null;
}

/** Admin payload to set/clear a user's caps (null/omit = unlimited). */
export interface SetTokenCapRequest {
  dailyLimit?: number | null;
  monthlyLimit?: number | null;
}

/** One preview-interest note in a user's thread. */
export interface PreviewInterest {
  id: string;
  message: string;
  createdAt: string;
}

/** Agent-card audience: anyone, or signed-in members only. */
export type CardVisibility = "private" | "discoverable";

/** How auto-booked meetings reach a user: silently onto their calendar, or by email + .ics. */
export type BookingDelivery = "CALENDAR" | "EMAIL";

/** Subscription tier — gates booking-page caps. */
export type Tier = "FREE" | "BASIC" | "PRO" | "ENTERPRISE";

/** The two images a host can upload. */
export type MediaKind = "AVATAR" | "BANNER";

/**
 * The kept original behind each of them.
 *
 * <p>Never displayed. It exists so "Adjust" can reopen the cropper on what the host actually picked,
 * months after the file itself has been tidied off their desktop. Uploaded through the same routes as
 * everything else, so it is one more kind rather than a second mechanism.</p>
 */
export type MediaSourceKind = "AVATAR_SOURCE" | "BANNER_SOURCE";

export type AnyMediaKind = MediaKind | MediaSourceKind;

/** Which source kind carries the original for a displayed kind. */
export const SOURCE_KIND: Record<MediaKind, MediaSourceKind> = {
  AVATAR: "AVATAR_SOURCE",
  BANNER: "BANNER_SOURCE",
};

/** Largest upload we accept per kind, mirroring MediaKind on the backend. */
export const MEDIA_MAX_BYTES: Record<AnyMediaKind, number> = {
  AVATAR: 2 * 1024 * 1024,
  BANNER: 5 * 1024 * 1024,
  // Bounded to 2048px on its longest edge and re-encoded before it leaves the browser, so this is
  // headroom rather than a target.
  AVATAR_SOURCE: 5 * 1024 * 1024,
  BANNER_SOURCE: 5 * 1024 * 1024,
};

/** Image types we serve. Anything else is refused before an upload is signed. */
export const MEDIA_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Image types a host may PICK, which is a longer list than the one above.
 *
 * <p>The two differ because of what the cropper does in between: everything a host chooses is
 * decoded, framed and re-encoded to WebP in the browser, so the format they picked never reaches
 * storage. HEIC is the whole reason the distinction is worth having — it is the camera default on
 * every iPhone, so refusing it means refusing the most common photograph on earth, while accepting
 * it costs the server nothing at all.</p>
 *
 * <p>Shaped for react-dropzone's {@code accept}, which wants extensions alongside the MIME types.
 * They are not redundant: a browser that cannot decode HEIC often cannot label it either, and hands
 * over a file with an empty type where only the name gives it away.</p>
 */
export const MEDIA_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
};

/**
 * Largest file a host may pick, before framing.
 *
 * <p>Far above {@link MEDIA_MAX_BYTES}, and measuring a different thing: this is the file leaving
 * their disk, that one is the framed WebP arriving at storage. A 40MP HEIC comfortably exceeds the
 * stored limit and comfortably lands under it once framed, so checking the upload limit at pick time
 * would reject photographs that were always going to be fine.</p>
 */
export const MEDIA_PICK_MAX_BYTES = 40 * 1024 * 1024;

/** Permission to upload one image straight to storage (POST /media/{kind}/uploads). */
export interface MediaUploadTicket {
  key: string;
  url: string;
  /** Must be sent on the PUT verbatim — they are covered by the signature. */
  headers: Record<string, string>;
  expires_at: string;
}

/** One of the host's images after a change; url is null when there is none. */
export interface MediaView {
  kind: AnyMediaKind;
  url: string | null;
}

/** Social networks a host can publish a profile link for. Mirrors the backend SocialNetwork enum. */
export type SocialNetwork = "LINKEDIN" | "INSTAGRAM" | "X" | "FACEBOOK";

/**
 * One published social profile. `url` is canonical and host-checked by the server, so it is safe to
 * render as an outbound link; `label` is what to show (the handle, usually).
 */
export interface SocialLink {
  network: SocialNetwork;
  url: string;
  label: string;
}
export const TIERS: Tier[] = ["FREE", "BASIC", "PRO", "ENTERPRISE"];

/** Admin payload to set a user's tier. */
export interface SetTierRequest {
  tier: Tier;
}

/**
 * One tier's admin-tunable booking caps, across all three offerings.
 *
 * `max_active_pages` is the AD-HOC live count — it keeps that name because it only ever counted
 * booking pages. Rolling links carry no monthly cap at any tier, by design, so there is no
 * `max_rolling_per_month`.
 */
export interface BookingTierLimitView {
  tier: Tier;
  max_active_pages: number;
  max_active_rolling: number;
  max_active_polls: number;
  max_adhoc_per_month: number;
  max_polls_per_month: number;
  max_bookings_per_page: number;
  max_link_days: number;
  max_ranges: number;
}

/** Admin edit to a tier's caps; omitted fields unchanged. */
export interface BookingTierLimitUpdateRequest {
  max_active_pages?: number;
  max_active_rolling?: number;
  max_active_polls?: number;
  max_adhoc_per_month?: number;
  max_polls_per_month?: number;
  max_bookings_per_page?: number;
  max_link_days?: number;
  max_ranges?: number;
}

/** The caller's own effective booking caps (from their tier) — for the builder. */
export interface MyBookingLimits {
  tier: Tier;
  max_active_pages: number;
  max_bookings_per_page: number;
  max_link_days: number;
  min_slot_minutes: number;
  max_slot_minutes: number;
  max_ranges: number;
}

/**
 * Body for POST /api/v1/profile (first-time setup). Backend validates the
 * handle format and uniqueness; we also validate format client-side so the
 * user gets immediate feedback before paying for a round-trip.
 */
export interface ProfileSetupRequest {
  handle: string;
  firstName: string;
  lastName: string;
  timezone?: string | null;
  /** Onboarding captures discoverability; defaults to "discoverable" in the SPA. */
  cardVisibility?: CardVisibility;
}

/**
 * Body for PATCH /api/v1/profile (name + card edits; handle is immutable in v1).
 * Availability patterns are edited through /api/v1/availability-patterns, not here.
 */
export interface ProfileUpdateRequest {
  firstName?: string | null;
  lastName?: string | null;
  timezone?: string | null;
  /** Replace-set: send [] to clear, omit to keep. */
  cardInterests?: string[];
  cardVisibility?: CardVisibility;
  bookingDelivery?: BookingDelivery;
  /** Whether people may reach this host through an agent. Omit to leave unchanged. */
  a2aHostEnabled?: boolean;
  /**
   * Replace-set, like cardInterests. Each `value` is whatever the host typed — a handle or a
   * profile URL — and the server canonicalises it. A blank value removes that network.
   */
  socialLinks?: { network: SocialNetwork; value: string }[];
}

/**
 * GET /api/v1/cards/{handle} — the public agent-card surface. Owner-curated:
 * no email, no calendar detail. ownerFirstName falls back to "@handle" so
 * the greeting always reads naturally.
 */
export interface AgentCardView {
  handle: string;
  ownerFirstName: string;
  /**
   * Names of the availability patterns this person books with (e.g. "Work default").
   * Informational — types are their own outbound templates, so this no longer
   * constrains what a visitor may request. Their availability is never published.
   */
  availabilityPatterns: string[];
  interests: string[];
  timezone: string | null;
}

/** One connected external calendar (GET /integrations). */
export interface IntegrationView {
  provider: string;
  /** ACTIVE, or ERROR when the provider rejected our refresh token. */
  status: "ACTIVE" | "ERROR";
  externalEmail: string | null;
  /**
   * WORK or PERSONAL for Microsoft, null otherwise. A personal Microsoft account syncs a
   * calendar but cannot host Teams meetings, so Teams stays gated for one.
   */
  accountType: "WORK" | "PERSONAL" | null;
  /**
   * Capabilities the host's consent did not grant. Independent of `status`: a connection can be
   * ACTIVE with a perfectly valid refresh token and still be unable to read free/busy, because
   * consent is granular and an unticked box is simply absent from the grant.
   *
   * Empty covers both "everything granted" and "connected before we recorded the grant", which is
   * why an empty list means show the plain Connected badge rather than a warning.
   *
   * Optional so a browser holding a cached bundle keeps working while the backend rolls out.
   */
  missingCapabilities?: CalendarCapability[];
  connectedAt: string;
}

/** What a connected calendar has to be able to do. Mirrors the backend enum. */
export type CalendarCapability = "AVAILABILITY" | "EVENTS";

/**
 * GET /api/v1/integrations. `available` lists the providers this deployment can connect;
 * empty means none is configured and the section stays hidden.
 *
 * `googleAvailable` is the pre-`available` shape, still sent so a cached bundle keeps working
 * across the deploy that adds a second provider. Read `available`.
 */
export interface IntegrationsView {
  googleAvailable: boolean;
  available?: string[];
  connections: IntegrationView[];
}

/**
 * The external calendars a host can connect, mirroring the backend enum. Declared here because it
 * is part of the wire contract; `@/lib/calendarProviders` aliases it and owns the display names.
 */
export type CalendarProvider = "GOOGLE" | "MICROSOFT";

/** GET /integrations/{provider}/connect — consent URL to navigate to. */
export interface ConnectUrlView {
  authorizationUrl: string;
}

/**
 * One imported external-calendar event (GET /integrations/external-events),
 * shown read-only on the Delgentic calendar. Not a meeting.
 */
export interface ExternalEventView {
  id: string;
  title: string;
  start: string; // ISO instant
  end: string; // ISO instant
  allDay: boolean;
  /** Which calendar it was imported from. Name it with `calendarProvider()`, never by hand. */
  provider: CalendarProvider;
}

/** GET /api/v1/entitlements — the current user's plan, trial, quota usage + features. */
export interface EntitlementQuota {
  key: string;
  used: number;
  limit: number | null; // null = unlimited
  unlimited: boolean;
}
export interface EntitlementsView {
  tier: string;
  trialActive: boolean;
  trialEndsAt: string | null;
  quotas: EntitlementQuota[];
  features: string[];
}

/** Admin: one tier's editable entitlement policy. A quota of -1 = unlimited. */
export interface EntitlementTierLimitView {
  tier: string;
  availability_patterns: number;
  monthly_bookings: number;
  custom_branding: boolean;
  analytics: boolean;
  audit_log: boolean;
  ai_scheduling: boolean;
  a2a_marketplace: boolean;
}
export type EntitlementTierLimitUpdate = Partial<Omit<EntitlementTierLimitView, "tier">>;

/**
 * User-facing plan catalogue row (GET /entitlements/plans). Only what is actually enforced, in the
 * same shape the public pricing table sells it: counts per offering, not one number for all three.
 * Rolling links have no monthly cap at any tier, so there is no rolling_per_month.
 */
export interface PlanView {
  tier: string;
  availability_patterns: number;
  adhoc_live: number;
  adhoc_per_month: number;
  rolling_live: number;
  polls_live: number;
  polls_per_month: number;
  link_days: number;
  custom_branding: boolean;
  audit_log: boolean;
  ai_scheduling: boolean;
  /**
   * True while the tier is in beta testing: shown with everything it includes, badged, and switchable
   * only by an admin. Set from BetaTiers on the server so the page never keeps its own tier list.
   */
  beta: boolean;
}

/**
 * One confirmed booking-page booking (GET /booking-pages/bookings), shown
 * read-only + translucent on the meetings calendar. Not a meeting.
 */
export interface BookingSlotView {
  id: string;
  bookingPageId: string; // which page this slot belongs to — lets us deep-link to it
  title: string; // the booking page's title
  inviteeName: string | null;
  start: string; // ISO
  end: string; // ISO
}

/**
 * One confirmed rolling-link appointment (GET /rolling-links/appointments), shown read-only +
 * translucent on the meetings calendar. The rolling twin of {@link BookingSlotView}.
 */
export interface RollingAppointmentSlotView {
  id: string;
  rollingLinkId: string; // which link this appointment belongs to
  title: string; // the rolling link's title
  inviteeName: string | null;
  start: string; // ISO
  end: string; // ISO
}

/**
 * Result of a manual import / current cached state
 * (POST /integrations/google/import, GET /integrations/import-status).
 */
export interface ImportStatusView {
  count: number;
  lastImportedAt: string | null;
}

/** Weekday order for rendering toggles + building day lists. */
export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/**
 * Handle format the backend enforces: 3–30 lowercase letters and digits,
 * nothing else. Re-asserted client-side so the form can show inline
 * guidance before the user submits. Mirrors the {@code HANDLE_FORMAT}
 * pattern in ProfileController. (Legacy hyphenated handles still exist
 * until their owners take the one-time rename — that's why MENTION_RE in
 * mentions.ts is deliberately looser than this.)
 */
export const HANDLE_REGEX = /^[a-z0-9]{3,30}$/;

export type ParserStatus = "ok" | "needs_clarification" | "unsupported";

export type NegotiationStatus =
  | "OPEN"
  | "ACCEPTED"
  | "FAILED"
  | "EXPIRED"
  | "CANCELLED"
  | "AWAITING_INITIATOR";

export type ParticipantState =
  | "PENDING"
  | "PROPOSED"
  | "COUNTERED"
  | "ACCEPTED"
  | "REJECTED"
  | "CANCELLED"
  | "DECLINED"
  | "INCOMPATIBLE_SCHEDULE"
  | "NO_RESPONSE";

export type MessageDirection = "INBOUND" | "OUTBOUND";

export type WireMessageType =
  | "propose_slots"
  | "counter_propose"
  | "accept"
  | "reject"
  | "give_up";

// ---------- /messages ----------

/**
 * POST /api/v1/messages body. {@code timeZone} is an IANA name from
 * {@code Intl.DateTimeFormat().resolvedOptions().timeZone} — lets the
 * backend parser interpret times in the user's local clock rather than UTC.
 */
export interface ParticipantRef {
  handle: string;
  required: boolean;
}

/** A quick-reply choice: display `label` + the `value` the server acts on. */
export interface ClarificationOption {
  label: string;
  value: string;
}

export interface MessageRequest {
  text: string;
  timeZone?: string;
  /**
   * Set only when continuing an availability-pattern clarification: echo the
   * conversation_id the backend returned with a `needs_clarification` reply so
   * it merges this message as the answer. Omit for a fresh request.
   */
  conversation_id?: string;
  /**
   * `@handle` tokens pre-extracted from the chat input. When present the
   * backend resolves participants by handle instead of by names the parser
   * pulls from prose. Omit when the message has no `@`-mentions.
   */
  participants?: ParticipantRef[];
  /**
   * The `value` of a quick-reply the user clicked (from a prior
   * `clarification_options` entry), e.g. an encoded time window. Lets the
   * server apply the choice deterministically. Omit for a typed message.
   */
  selected_option_value?: string;
  /**
   * The chat-window confirmation toggle: `pre_confirm` makes the backend echo
   * its interpretation and wait for a go-ahead before contacting other agents;
   * `auto` proceeds, confirming only when the interpretation is a guess. Omit
   * to let the backend default to `auto`.
   */
  confirmation_mode?: ConfirmationMode;
}

/** Whether the backend confirms its interpretation before contacting other agents. */
export type ConfirmationMode = "auto" | "pre_confirm";

/** Which input widget a clarification wants the SPA to render. */
export type ClarificationKind = "choice" | "date_range" | "text" | "people";

/** The built-up "meeting so far" shown as a card next to a confirmation. */
export interface MeetingInterpretation {
  participants: string[];
  start?: string | null;
  end?: string | null;
  weekdays?: string[];
  subject?: string | null;
  duration_minutes?: number | null;
  time_of_day?: string | null;
}

export interface MessageResponse {
  conversation_id: string | null;
  reply: string;
  status: ParserStatus;
  /** Structured quick-reply choices on a `needs_clarification` reply. */
  clarification_options?: ClarificationOption[];
  /** Which input widget to render for this clarification (default: choice). */
  clarification_kind?: ClarificationKind;
  /** The parsed meeting so far, to show as a context card (on a confirmation). */
  interpretation?: MeetingInterpretation;
}

/**
 * What one web turn came back as, from `POST /turns`. The server routes the sentence (design
 * 5.1: the browser decides nothing), and `channel` says which pipeline read it. The two halves
 * are the unchanged `/messages` and `/cohort-intents` response shapes.
 */
export type TurnResponse =
  | { channel: "chat"; chat: MessageResponse }
  | { channel: "cohort"; batch: CohortBatchView };

// ---------- /conversations/{id} ----------

export interface ConversationView {
  id: string;
  status: NegotiationStatus;
  parsed_request: unknown;
  current_round: number;
  decided_slot: { start: string; end: string } | null;
  participants: ConversationParticipant[];
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
}

export interface ConversationParticipant {
  agent_id: string;
  display_name: string;
  state: ParticipantState;
}

export interface ConversationMessage {
  round: number;
  direction: MessageDirection;
  message_type: WireMessageType;
  from_agent: string;
  to_agent: string;
  payload: unknown;
  at: string;
}

// ---------- /meetings ----------

export interface MeetingView {
  conversation_id: string;
  state: NegotiationStatus;
  decided_slot: { start: string; end: string } | null;
  with: string[];
  /**
   * The initiator's own label for this meeting, e.g. "Work default". Null on a
   * meeting someone else invited us to: how they categorised it is theirs.
   */
  pattern_label: string | null;
  /** Short title parsed from the request, or null. */
  subject: string | null;
  /** Longer free-text context, or null. */
  detail: string | null;
  /** Recurring-series id this meeting is an occurrence of, or null. */
  series_id: string | null;
  /** This occurrence's date within its series, or null. */
  occurrence_date: string | null;
  created_at: string;
  accepted_at: string | null;
  /** True when a later reschedule superseded this negotiation; hidden by default. */
  superseded?: boolean;
  /** Display name of the original creator (stable across reschedules), or null. */
  created_by?: string | null;
  /** True when the signed-in user is the meeting's creator. */
  created_by_me?: boolean;
  /** Per-counterparty state — group meetings render these as chips. */
  participant_states?: ParticipantStateView[];
  /** Slots participants offered; the initiator's menu when AWAITING_INITIATOR. */
  counter_options?: { start: string; end: string }[];
  /**
   * The meeting this chain started from. Every reschedule of the same meeting
   * shares it, so the UI can group a meeting with its earlier attempts.
   */
  root_meeting_id?: string | null;
  /**
   * The negotiation this one is trying to replace, or null when it isn't a
   * reschedule. Non-null on an in-flight or failed attempt too — the original
   * stays booked until a new time is agreed.
   */
  rescheduled_from?: string | null;
  /**
   * True when YOU triggered a reschedule off this meeting's current slot (from your
   * own blackout). Only the person who asked sees the "…the window you asked for…"
   * failure banner; others get a transient toast instead.
   */
  rescheduled_by_me?: boolean;
}

/** A counterparty's per-meeting status. */
export interface ParticipantStateView {
  name: string;
  state: ParticipantState;
  required: boolean;
}

// ---------- /relationships ----------

/**
 * A slot the signed-in user ruled out for this meeting chain — the time they
 * rescheduled away from. Per-user: you never see the other party's.
 */
export interface BlackoutView {
  start: string;
  end: string;
  reason: string | null;
}

/** An availability pattern the caller has filed this person under. */
export interface RelationshipTypeRef {
  id: string;
  name: string;
}

/**
 * One person in the caller's "Booked with" panel, with every one of the caller's
 * availability patterns they are filed under. An empty `types` list is the
 * Uncategorized bucket: they invited the caller, who never chose a type for them.
 */
export interface RelationshipView {
  /** The other party's @handle (without the @), or null if unset. */
  handle: string | null;
  display_name: string;
  /** The peer's user id — needed to file/unfile them from a type. */
  peer_user_id: string;
  /** True when this peer has a card the caller may open; false → don't link. */
  has_card: boolean;
  /** ISO timestamp of the most recent booking, or null when connected but not yet booked. */
  last_booked_at: string | null;
  /** Accepted meetings with this person, summed across buckets; 0 = not yet booked. */
  meeting_count: number;
  /** Empty ⇒ Uncategorized. */
  types: RelationshipTypeRef[];
}

// ---------- /contacts/search ----------

/** One @-mention autocomplete suggestion from {@code GET /contacts/search}. */
export interface MentionCandidate {
  /** The user's @handle, without the leading @. */
  handle: string;
  display_name: string;
  /** Accepted meetings shared with the caller; 0 for public-directory matches. */
  meeting_count: number;
  /** "booked" = met before (ranked by frequency); "public" = publicly-visible handle. */
  source: "booked" | "public";
}

// ---------- /connections ----------

export type ConnectionBox = "received" | "sent" | "accepted";
export type ConnectionRequestStatus = "pending" | "accepted" | "declined";

/** One connection request from the current user's point of view (GET /connections). */
export interface ConnectionView {
  id: string;
  status: ConnectionRequestStatus;
  /** "sent" = I asked them; "received" = they asked me. */
  direction: "sent" | "received";
  /** The other party's handle (without the leading @). */
  peerHandle: string | null;
  peerDisplayName: string | null;
  createdAt: string;
  respondedAt: string | null;
}

/** Ack for a meeting action (cancel / reschedule / resolve). */
export interface MeetingActionResponse {
  conversation_id: string | null;
  state: NegotiationStatus;
  reply: string;
}

/** Body of POST /meetings/{id}/resolve. */
export interface ResolveRequest {
  start_times: string[];
}

/** An explicit clock-time band the user picked, applied per-day across the date span. */
export interface TimeOfDayWindow {
  start: string; // HH:MM
  end: string; // HH:MM
  /** When true, overrides the availability-pattern window on both sides (a conscious exception). */
  priority: boolean;
}

/** Body of POST /meetings/{id}/reschedule. */
export interface RescheduleRequest {
  time_window: { start: string; end: string };
  start_time?: string | null;
  time_zone?: string | null;
  /** Optional "between 14:30 and 16:30" band; a reschedule band is always a priority exception. */
  time_of_day?: TimeOfDayWindow | null;
}

// ---------- SSE DomainEvent shapes ----------

/**
 * Enriched agent identity carried on SSE events. The backend looks up each
 * AgentRef (UUID-only in the engine) against AgentRepository before
 * serialising, so the SPA can render real names without an extra round trip.
 *
 * `handle` is null until Phase 1 (DESIGN.md §Phasing) adds the column to
 * app_user. SPA should fall back to `displayName` when handle is null.
 */
export interface AgentInfo {
  id: string;
  displayName: string | null;
  handle: string | null;
}

export type DomainEvent =
  | {
      type: "NegotiationOpened";
      data: { conversationId: string; participants: AgentInfo[] };
    }
  | { type: "RoundAdvanced"; data: { conversationId: string; round: number } }
  | {
      type: "CounterReceived";
      data: {
        conversationId: string;
        from: AgentInfo;
        slots: { start: string; end: string }[];
      };
    }
  | { type: "SlotAccepted"; data: { conversationId: string; slot: { start: string; end: string } } }
  | { type: "NegotiationFailed"; data: { conversationId: string; reason: string } };

export const KNOWN_EVENT_TYPES: DomainEvent["type"][] = [
  "NegotiationOpened",
  "RoundAdvanced",
  "CounterReceived",
  "SlotAccepted",
  "NegotiationFailed",
];

/**
 * Events the per-user SSE stream emits. Superset of DomainEvent — adds
 * ConversationInvited (one-shot, fired when the user's agent first
 * appears as a non-initiator participant). The wire format reuses the
 * existing AgentInfo / Slot shapes.
 */
export type UserEvent =
  | {
      type: "ConversationInvited";
      data: {
        conversationId: string;
        initiator: AgentInfo;
        participants: AgentInfo[];
      };
    }
  | DomainEvent;

export const KNOWN_USER_EVENT_TYPES: UserEvent["type"][] = [
  "ConversationInvited",
  ...KNOWN_EVENT_TYPES,
];

/**
 * One row in {@code GET /api/v1/conversations/incoming}. A negotiation the
 * current user's agent is on as a participant but did not initiate.
 *
 * <p>{@code last_inbound_event_at} is the SPA's cursor for the Incoming-
 * toggle activity dot — we compare it against a locally-stored "last seen"
 * timestamp to detect activity that happened between polls.</p>
 */
export interface IncomingConversationView {
  conversation_id: string;
  status: NegotiationStatus;
  initiator: AgentInfo;
  participants: AgentInfo[];
  last_inbound_event_at: string | null;
  /**
   * True when this negotiation was cancelled only because a reschedule of it was
   * accepted — the meeting MOVED, it wasn't called off. The poller uses it to
   * suppress the misleading "couldn't reach agreement" toast; the accepted
   * successor announces the new time itself.
   */
  superseded_by_reschedule?: boolean;
}

/**
 * One line the user reads before agreeing to anything.
 *
 * `caller_role` is what YOU are to that meeting, and it is per row because one cohort can mix the
 * two: cancelling a meeting you host calls it off for everyone, cancelling one you merely attend
 * withdraws your own seat.
 */
export type CohortTargetView = {
  meeting_id?: string;
  person_id?: string;
  title: string;
  starts_at?: string;
  caller_role?: "HOST" | "PARTICIPANT";
  conversation_id?: string;
};

/**
 * One card. A sentence with two verbs produces two of these, each confirmed on its own.
 *
 * `dropped_targets` is what this matched on the PREVIOUS turn and no longer matches. It is for the
 * current turn only: "7 meetings" becoming "4" is not an answer to "only the recurring ones" unless
 * you can see which three went.
 *
 * `confirmable` says whether the button should be there at all. Every mutating verb confirms;
 * FIND_TIME never does, because its card is an answer rather than a proposal (a Confirm on a
 * result would agree to nothing). Also false while a card still has a question (NEEDS_INPUT) or
 * matched nobody.
 *
 * `filter_not_understood` is true when the intent matched nothing because its filter contains a
 * condition the server could not answer. The card must then say "could not tell what to match"
 * rather than show an empty list that reads like "you have none".
 */
export type CohortIntentView = {
  id: string;
  ordinal: number;
  verb: "SCHEDULE" | "RESCHEDULE" | "CANCEL" | "FIND_TIME";
  status: "PROPOSED" | "NEEDS_INPUT" | "DROPPED" | "ACTED";
  targets: CohortTargetView[];
  dropped_targets: CohortTargetView[];
  conversation_count: number;
  confirmable: boolean;
  filter_not_understood?: boolean;
  /**
   * While NEEDS_INPUT: what this card is missing. Answer with this card's id as intent_id.
   * `options` is the answer-space when it is a closed list (the pattern question offers the
   * caller's own pattern labels), rendered as quick replies.
   */
  needs?: { field: string; question: string; options?: string[] }[];
  /**
   * Only on a FIND_TIME card: the answer itself. A result, not a proposal — the card has no
   * Confirm. `free` is when the caller is free inside the searched window; `meetings` is what
   * constrains those times. `scope_missing` names the token scope a PAT caller lacks, so a
   * withheld calendar never reads as an empty one.
   */
  find_time?: {
    free: { start: string; end: string }[];
    meetings: CohortTargetView[];
    scope_missing?: string;
  };
};

/**
 * A sentence, read back, with nothing done about it.
 *
 * `question` is set when the model asked something instead of proposing. On a fresh sentence that
 * is all there is and `id` is absent, because nothing was worth storing.
 */
export type CohortBatchView = {
  id?: string;
  status?: "PENDING_CONFIRMATION" | "CONFIRMED" | "WITHDRAWN" | "EXPIRED";
  raw_text?: string;
  expires_at?: string;
  intents: CohortIntentView[];
  question?: string;
};

/**
 * One row in {@code GET /api/v1/chat-log}. The server merges user/agent NL
 * chat with A2A wire events filtered to the user's conversations. The
 * {@code kind} discriminator tells the SPA which payload shape to expect.
 *
 * <p>Each kind is its own variant — TS narrowing on {@code row.kind} only
 * locks the payload type when the discriminant is a single literal per
 * variant.</p>
 */
export type ChatLogEntryView =
  | {
      id: string;
      kind: "USER_MSG";
      occurred_at: string;
      conversation_id: string | null;
      payload: { text: string };
    }
  | {
      id: string;
      kind: "AGENT_REPLY";
      occurred_at: string;
      conversation_id: string | null;
      payload: { text: string };
    }
  | {
      id: string;
      kind: "A2A_EVENT";
      occurred_at: string;
      conversation_id: string | null;
      payload: {
        message_type: string;
        direction: "INBOUND" | "OUTBOUND";
        round: number;
        from: AgentInfo | null;
        to: AgentInfo | null;
        raw: unknown;
      };
    }
  | {
      id: string;
      kind: "COHORT_PROPOSAL";
      occurred_at: string;
      /**
       * Always null on this kind, deliberately. The panel renders a "View negotiation" link for any
       * agent entry carrying a conversation id, and a cohort batch is not a negotiation.
       */
      conversation_id: null;
      payload: { batch: CohortBatchView };
    };

// ---- Admin LLM playground (camelCase; admin-internal endpoints) ----

/** One DB-backed, editable LLM prompt (GET/PUT /admin/llm/prompts). */
export interface AdminPromptInfo {
  key: string;
  label: string;
  description: string;
  content: string;
  /** True when the saved content differs from the bundled default. */
  customized: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface LlmTurnRequest {
  text: string;
  timeZone?: string;
  provider?: string; // OPENAI_COMPAT | BEDROCK | OLLAMA — omit for the deployed default
  model?: string;
  temperature?: number;
  region?: string;
  systemPrompt?: string; // override the shipped parser system prompt (live tuning)
}

/** A live turn's full exchange (POST .../turns). */
export interface LlmExchange {
  correlationId: string;
  seq: number;
  provider: string;
  model: string;
  latencyMs: number | null;
  systemPrompt: string;
  userMessage: string;
  rawResponse: string | null;
  extractedJson: unknown | null;
  parsed: unknown | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
}

export interface LlmRoundSummary {
  correlationId: string;
  startedAt: string;
  turns: number;
  provider: string | null;
  snippet: string | null;
}

/** A persisted turn (GET a round / by-negotiation). */
export interface LlmStoredTurn {
  seq: number;
  createdAt: string;
  provider: string | null;
  model: string | null;
  request: unknown | null;
  rawResponse: string | null;
  parsed: unknown | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
}

export interface ModelMessage {
  role: string;
  content: string;
}

/** A tool-mode turn: completed with JSON, or paused asking for clarification. */
export interface LlmToolExchange {
  correlationId: string;
  seq: number;
  provider: string;
  model: string;
  latencyMs: number | null;
  status: "COMPLETED" | "NEEDS_CLARIFICATION";
  question: string | null;
  conversation: ModelMessage[];
  modelJson: string | null;
  parsed: unknown | null;
}

/**
 * What the agent-request matcher did with a typed set of links.
 *
 * <p>`matched` is what it chose after being resolved against `offered` exactly and
 * case-insensitively, the same way admission resolves it. `rawAnswer` is what it said before that,
 * which is how you tell "it answered with something we rejected" from "it answered with nothing".</p>
 */
export interface AdmissionPreviewResult {
  provider: string;
  model: string;
  latencyMs: number | null;
  systemPrompt: string;
  request: string;
  /** The phrases actually put in front of the model, after truncation and de-duplication. */
  offered: string[];
  matched: string | null;
  explicit: boolean;
  rawAnswer: string | null;
  /** Whether it went and fetched the candidates at all, rather than answering blind. */
  toolWasCalled: boolean;
  /** Only an explicitly stated purpose ever books without a person (D-15). */
  wouldAutoBook: boolean;
  conversation: ModelMessage[];
}

export interface LlmToolResumeRequest {
  answer: string;
  conversation: ModelMessage[];
  timeZone?: string;
  provider?: string;
  model?: string;
  temperature?: number;
  region?: string;
  systemPrompt?: string;
}

// ---- Booking pages (#42) — snake_case on the wire ------------------------

export type BookingPageStatus = "ACTIVE" | "DISABLED" | "EXPIRED";

/** One absolute availability window (a drag on the host's calendar). ISO strings. */
export interface BookingWindow {
  start: string;
  end: string;
}

export interface BookingPageView {
  id: string;
  slug: string;
  url_path: string;
  title: string;
  description: string | null;
  slot_minutes: number;
  fluid: boolean;
  timezone: string;
  max_bookings: number | null;
  one_per_invitee: boolean;
  min_notice_minutes: number;
  valid_from: string | null;
  valid_until: string | null;
  allow_cancel: boolean;
  invite_signups: boolean;
  status: BookingPageStatus;
  booking_count: number;
  /**
   * When the link stops offering anything: the EARLIER of valid_until and the end of its
   * last slot. Read THIS, not valid_until — a link whose slots have all passed is finished
   * even with days left on valid_until. Null only if it has neither bound.
   */
  ends_at: string | null;
  /** Start of the soonest confirmed booking still ahead, or null when nothing is coming up. */
  next_booking_at: string | null;
  /** The availability pattern this page draws its recurring availability from, or null (ad-hoc windows only). */
  availability_pattern_id: string | null;
  /** Video provider a per-booking link is minted from (or null); and/or a free-text place. */
  conferencing_provider: ConferenceProvider | null;
  location: string | null;
  windows: BookingWindow[];
  created_at: string;
  /** Whether this link publishes the host's picture / banner. */
  show_host_avatar: boolean;
  show_host_banner: boolean;
}

/** A video-conferencing provider a booking page can nominate. Only GOOGLE_MEET is wired today. */
export type ConferenceProvider = "GOOGLE_MEET" | "MICROSOFT_TEAMS" | "ZOOM";

/** One bookable slot in a builder-side preview — what invitees see, busy already removed. */
export interface BookingPreviewSlot {
  start: string;
  end: string;
  /** Builder preview only: true when a meeting clash blocks this slot (open slots are false). */
  blocked?: boolean;
  /** Start (ISO) of the clashing meeting when blocked, else null. */
  conflict_at?: string | null;
}

export type BookingSlotState = "OPEN" | "BOOKED" | "BLOCKED" | "PAST";

/** A frozen slot of a created booking page with its current state (owner's read-only view). */
export interface BookingSlotStatus {
  start: string;
  end: string;
  status: BookingSlotState;
  /** Set on BOOKED slots only. What lets the host cancel from the slot itself. */
  booking_id?: string | null;
  invitee_first_name?: string | null;
  invitee_email?: string | null;
}

export interface BookingPageUpsertRequest {
  title: string;
  description?: string | null;
  slot_minutes: number;
  fluid?: boolean;
  timezone: string;
  max_bookings?: number | null;
  one_per_invitee?: boolean;
  min_notice_minutes?: number;
  valid_from?: string | null;
  valid_until?: string | null;
  allow_cancel?: boolean;
  /** Draw recurring availability from one of the owner's availability patterns (live link), or null (ad-hoc). */
  availability_pattern_id?: string | null;
  /** Video provider (enum name) a per-booking link is minted from, or null/omit for none. */
  conferencing_provider?: ConferenceProvider | null;
  /** Free-text meeting place, or null/omit. */
  location?: string | null;
  windows: BookingWindow[];
  /** Per-link branding. Omit to leave unchanged; both default on for a new link. */
  show_host_avatar?: boolean;
  show_host_banner?: boolean;
}

/** Ask the AI to draft a booking-page description (POST /booking-pages/draft-description). */
export interface DraftDescriptionRequest {
  title: string;
  /** The host's current description text; when set, the AI improves it rather than starting over. */
  description?: string;
  pattern_label?: string;
}

/** The drafted description (app markdown subset) + the tokens the draft cost. */
export interface DraftedDescriptionView {
  markdown: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
}

/** One day's token total (ISO YYYY-MM-DD) for the profile heatmap. */
export interface TokenUsagePoint {
  date: string;
  tokens: number;
}

/** The current user's daily token usage over a trailing window — the profile heatmap series. */
export interface TokenUsageHeatmapView {
  range_days: number;
  total: number;
  days: TokenUsagePoint[];
}

/** The invitee's self-service view of their own booking (GET /book/manage/{token}). */
export interface PublicBookingManageView {
  title: string;
  host_name: string;
  start: string;
  end: string;
  timezone: string;
  status: "confirmed" | "cancelled";
  allow_cancel: boolean;
  cancellable: boolean;
  /** The original /book/{slug} link — to pick a new slot after cancelling. */
  booking_url: string;
}

export interface BookingView {
  id: string;
  start: string;
  end: string;
  invitee_name: string | null;
  invitee_email: string;
  note: string | null;
  /**
   * BOUNCED = the invitee's confirmation hard-bounced, which frees the slot. The host
   * endpoint returns every status unfiltered, so this arrives today.
   */
  status: "CONFIRMED" | "CANCELLED" | "BOUNCED";
  /** The generated join link + the provider it came from + a free-text place. Any nullable. */
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  location: string | null;
  created_at: string;
}

/** One entry in a booking link's journal (GET /booking-pages/{id}/events). */
export interface BookingEventView {
  id: string;
  /** Set for booking-level events (a specific booking); null for link-level ones. */
  booking_id: string | null;
  /** Mirrors `BookingEventType.java`. A value missing here is a value the trail cannot draw. */
  type:
    | "BOOKED"
    | "CANCELLED_BY_INVITEE"
    | "CANCELLED_BY_HOST"
    | "BOUNCED"
    | "LINK_CREATED"
    | "LINK_ENABLED"
    | "LINK_DISABLED"
    | "LINK_EXPIRED"
    | "LINK_DELETED"
    | "ADMIN_DISABLED"
    | "ADMIN_DELETED";
  actor: "INVITEE" | "HOST" | "SYSTEM" | "ADMIN";
  actor_email: string | null;
  reason: string | null;
  occurred_at: string;
}

/** One open slot the invitee can pick. */
export interface PublicBookingSlot {
  start: string;
  end: string;
}

/** What an anonymous invitee sees at /book/{slug} — the only address a booking page has. */
export interface PublicBookingPageView {
  /** The canonical booking slug — booking always POSTs here, however the page was reached. */
  slug: string;
  title: string;
  description: string | null;
  host_name: string;
  /** The host's picture, or null when their plan does not include publishing it. */
  host_avatar_url: string | null;
  /** The host's header image, or null. */
  host_banner_url: string | null;
  /** The host's social profiles, in their chosen order. Free at every tier. */
  host_social_links: SocialLink[];
  slot_minutes: number;
  timezone: string;
  bookable: boolean;
  unavailable_reason: string | null;
  /** Shown BEFORE booking so the invitee knows how they'll meet — provider name and/or a place.
   *  The actual join link is never here; it's minted only on confirmation. Both nullable. */
  conferencing_provider: ConferenceProvider | null;
  location: string | null;
  slots: PublicBookingSlot[];
  /** @see SlotPagingBounds — machine-readable only, never rendered. */
  range_from?: string;
  range_to?: string;
  availability_from?: string | null;
  availability_to?: string | null;
}

export interface BookSlotRequest {
  start: string;
  name?: string;
  email: string;
  note?: string;
  /** Honeypot — must stay empty. */
  website?: string;
}

export interface PublicBookingConfirmationView {
  title: string;
  host_name: string;
  start: string;
  end: string;
  timezone: string;
  invitee_email: string;
  /** The join link minted at this confirmation + its provider, and/or a free-text place. Any nullable. */
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  location: string | null;
}

// ---- Rolling link — snake_case on the wire -----------------------------
// A distinct offering from the ad-hoc booking page: a mutable, always-on link
// (namespaced under the owner's @handle) whose slots are generated on the fly.

export type RollingLinkStatus = "ACTIVE" | "DISABLED";

/**
 * How a rolling link's minimum notice is read. MINUTES is a fixed rolling offset (the stored
 * min_notice_minutes); the "END_OF_…" kinds are relative cutoffs resolved in the rolling link's timezone
 * (end of day → tomorrow 00:00, end of next day → the day after, end of week → next Monday).
 */
export type RollingLinkNoticeKind = "MINUTES" | "END_OF_DAY" | "END_OF_NEXT_DAY" | "END_OF_WEEK";

/** How the availability pattern repeats. */

/**
 * One recurring band. weekday: 0 = Sunday … 6 = Saturday; local "HH:mm". week_index is 0 for weekly
 * (and Week A of a biweekly), 1 for Week B of a biweekly pattern.
 */
export interface RollingLinkRange {
  weekday: number;
  earliest: string;
  latest: string;
}

/** The host-facing view of one rolling link. */
export interface RollingLinkView {
  /** The availability pattern this link owns. Admission rules for the link hang off it. */
  availability_pattern_id: string | null;
  id: string;
  handle: string;
  /** The branded public path the rolling link is served at: "@owner/rolling link/{handle}". */
  public_path: string;
  title: string;
  description: string | null;
  slot_minutes: number;
  timezone: string;
  min_notice_minutes: number;
  notice_kind: RollingLinkNoticeKind;
  interval_minutes: number;
  valid_until: string | null;
  allow_multi_appointment: boolean;
  conferencing_provider: ConferenceProvider | null;
  location: string | null;
  status: RollingLinkStatus;
  created_at: string;
  updated_at: string;
  /** Confirmed appointments booked on this rolling link (for the host's link lists). */
  booking_count: number;
  /** Start of the soonest confirmed appointment still ahead, or null when nothing is coming up. */
  next_booking_at: string | null;
  availability: RollingLinkRange[];
  /** Whether this link publishes the host's picture / banner. */
  show_host_avatar: boolean;
  show_host_banner: boolean;
}

/** Host create/update body. Handle is ignored on update (fixed once minted). */
export interface RollingLinkUpsertRequest {
  handle: string;
  title: string;
  description?: string | null;
  slot_minutes: number;
  timezone: string;
  min_notice_minutes?: number;
  /** "MINUTES" (fixed) or a relative "END_OF_DAY"/"END_OF_NEXT_DAY"/"END_OF_WEEK" cutoff. Null → MINUTES. */
  notice_kind?: RollingLinkNoticeKind;
  /** "WEEKLY" or "BIWEEKLY". Null → WEEKLY. */
  /** Spacing between slot starts; defaults to slot_minutes. Must be >= slot_minutes. */
  interval_minutes?: number;
  /** Optional end date (capped ~12 months out); omit for rolling to the default horizon. */
  valid_until?: string | null;
  allow_multi_appointment?: boolean;
  conferencing_provider?: ConferenceProvider | null;
  location?: string | null;
  availability: RollingLinkRange[];
  /** Per-link branding. Omit to leave unchanged; both default on for a new link. */
  show_host_avatar?: boolean;
  show_host_banner?: boolean;
}

/** The host-facing view of one booked rolling link appointment. */
export interface RollingAppointmentView {
  id: string;
  start: string;
  end: string;
  invitee_email: string;
  invitee_name: string | null;
  note: string | null;
  status: "CONFIRMED" | "CANCELLED";
  group_id: string | null;
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  location: string | null;
  created_at: string;
}

/** One open slot the invitee can pick. */
export interface PublicRollingLinkSlot {
  start: string;
  end: string;
  /** Builder preview only: true when a meeting clash blocks this slot (open slots are false). */
  blocked?: boolean;
  /** Start (ISO) of the clashing meeting when blocked, else null. */
  conflict_at?: string | null;
}

/** What an anonymous invitee sees at /@owner/rolling link/{handle}. */
export interface PublicRollingLinkView {
  title: string;
  description: string | null;
  host_name: string;
  /** The host's picture, or null when their plan does not include publishing it. */
  host_avatar_url: string | null;
  /** The host's header image, or null. */
  host_banner_url: string | null;
  /** The host's social profiles, in their chosen order. Free at every tier. */
  host_social_links: SocialLink[];
  timezone: string;
  slot_minutes: number;
  allow_multi_appointment: boolean;
  location: string | null;
  slots: PublicRollingLinkSlot[];
  /** @see SlotPagingBounds — machine-readable only, never rendered. */
  range_from?: string;
  range_to?: string;
  availability_from?: string | null;
  availability_to?: string | null;
}

/**
 * What the invitee's calendar is allowed to ask for, and how far its arrows may go.
 *
 * `range_*` is the window the link will answer for at all; a request outside it is a 400.
 * `availability_*` is the narrower span that actually has times, so the arrows stop on the last
 * month with something in it rather than walking the invitee through empty months to the range end.
 * Null `availability_*` means the link has nothing to offer anywhere.
 *
 * **None of these may ever be displayed.** They are bounds the calendar obeys, not facts the invitee
 * reads: an invitee simply finds no further months, exactly as if the host had no more availability.
 * Rendering a bound would leak the host's plan, since two hosts with identical availability on
 * different tiers stop on different dates.
 *
 * Optional so a page served by a backend predating them still works — absent means "unbounded",
 * which is the pre-paging behaviour.
 */
/**
 * One month of a public link's slots. Omitted entirely on the first load, which asks for the link's
 * whole bookable range and is how the calendar learns where that range is.
 */
export interface SlotWindow {
  from: string;
  to: string;
}

export interface SlotPagingBounds {
  range_from?: string;
  range_to?: string;
  availability_from?: string | null;
  availability_to?: string | null;
}

/** Book one or more slots. More than one only when the rolling link allows it. */
export interface BookRollingLinkRequest {
  starts: string[];
  name?: string;
  email: string;
  note?: string;
  /** Honeypot — must stay empty. */
  website?: string;
}

/** One confirmed slot in the booking result. */
export interface RollingLinkConfirmationAppointment {
  start: string;
  end: string;
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  location: string | null;
}

export interface PublicRollingLinkConfirmationView {
  title: string;
  host_name: string;
  timezone: string;
  invitee_email: string;
  group_id: string | null;
  appointments: RollingLinkConfirmationAppointment[];
}

/** The invitee's tokenised view of their own appointment (cancel or reschedule). */
export interface RollingLinkManageView {
  title: string;
  host_name: string;
  start: string;
  end: string;
  timezone: string;
  status: "CONFIRMED" | "CANCELLED";
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  location: string | null;
  cancel_token: string;
  reschedule_token: string;
  /** Slots the invitee can move to (empty once cancelled/past). */
  open_slots: PublicRollingLinkSlot[];
}

export interface RescheduleRollingLinkRequest {
  new_start: string;
}

/**
 * What happened to a rolling link. CONFIG = the host's edits/lifecycle; BOOKING = anything that
 * happened to one appointment, by either party (the actor field says which).
 */
export type RollingLinkEventCategory = "CONFIG" | "BOOKING";

export type RollingLinkEventType =
  | "BOOKED"
  | "CANCELLED_BY_INVITEE"
  | "CANCELLED_BY_HOST"
  | "RESCHEDULED_BY_INVITEE"
  | "CLINIC_CREATED"
  | "CONFIG_CHANGED"
  | "ENABLED"
  | "DISABLED";

/** One entry in a rolling link's journal (GET /rolling links/{id}/events), newest first. */
export interface RollingLinkEvent {
  id: string;
  appointment_id: string | null;
  category: RollingLinkEventCategory;
  type: RollingLinkEventType;
  actor: "INVITEE" | "HOST" | "SYSTEM";
  actor_email: string | null;
  /** Config: a before→after summary; booking: the invitee's name. */
  detail: string | null;
  occurred_at: string;
}

// ---- Poll (booking offering #3) ----------------------------------------------

/**
 * Where a poll is. There is no separate DISABLED: the host's Enable/Disable toggles OPEN ↔ CLOSED,
 * because "not taking votes" and "disabled" are the same thing from every angle that matters. A
 * DELETED poll is never returned.
 */
export type PollStatus = "OPEN" | "CLOSED" | "CONFIRMED";

/** What someone thinks of one proposed time. No answer at all is a fourth state, and not a NO. */
export type PollVoteChoice = "YES" | "LESS_PREFERRED" | "NO";

/** One proposed time. `end_at` is always start + the poll's duration; the server computes it. */
export interface PollOption {
  id: string;
  start_at: string;
  end_at: string;
  position: number;
}

/** The host's view of one poll (GET/POST/PUT /polls, /polls/{id}). */
export interface Poll {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  location: string | null;
  conferencing_provider: ConferenceProvider | null;
  duration_minutes: number;
  timezone: string;
  /** Date only, read in the poll's timezone. Null = open until the host closes it. */
  respond_by: string | null;
  show_counts: boolean;
  status: PollStatus;
  /** Status AND the respond-by date together. Use this, not `status`, to decide "can I vote". */
  voting_open: boolean;
  confirmed_option_id: string | null;
  confirmed_at: string | null;
  /**
   * When the confirmation actually went out. Null with a CONFIRMED status is NORMAL: the host picks
   * the time first and chooses when everyone finds out.
   */
  confirmation_sent_at: string | null;
  meeting_provider: ConferenceProvider | null;
  meeting_url: string | null;
  created_at: string;
  updated_at: string;
  respondent_count: number;
  options: PollOption[];
  /** Whether this link publishes the host's picture / banner. */
  show_host_avatar: boolean;
  show_host_banner: boolean;
}

/** Create/update body. Only STARTS are sent — the server derives every end from the duration. */
export interface PollUpsertRequest {
  title: string;
  description?: string | null;
  location?: string | null;
  conferencing_provider?: string | null;
  duration_minutes: number;
  timezone: string;
  respond_by?: string | null;
  show_counts: boolean;
  starts: string[];
  /** Per-link branding. Omit to leave unchanged; both default on for a new link. */
  show_host_avatar?: boolean;
  show_host_banner?: boolean;
}

/** One column of the host's results grid. */
export interface PollOptionTally {
  option_id: string;
  start_at: string;
  end_at: string;
  yes: number;
  less_preferred: number;
  no: number;
  /** Derived (respondents − recorded). NOT a No: these people were never asked. */
  no_response: number;
  /** 1-based suggestion: most Yes, then most Less-preferred, then earliest. */
  rank: number;
}

/** One row. `votes` maps option id → choice; an absent key means they skipped that time. */
export interface PollRespondent {
  id: string;
  name: string;
  email: string;
  note: string | null;
  responded_at: string;
  votes: Record<string, PollVoteChoice>;
}

/** GET /polls/{id}/results — the host sees every name, address and choice. */
export interface PollResults {
  respondent_count: number;
  options: PollOptionTally[];
  respondents: PollRespondent[];
}

/**
 * What an anonymous invitee sees BEFORE they vote. Deliberately carries no vote counts at all —
 * they live only on {@link PollBallot}, which needs a manage token to reach.
 */
export interface PublicPoll {
  /**
   * The canonical slug, so someone who arrived by the branded alias still votes against the same
   * poll. It grants nothing extra: the alias they followed is public and resolves the same thing.
   */
  slug: string;
  title: string;
  description: string | null;
  host_name: string;
  /** The host's picture, or null when their plan does not include publishing it. */
  host_avatar_url: string | null;
  /** The host's header image, or null. */
  host_banner_url: string | null;
  /** The host's social profiles, in their chosen order. Free at every tier. */
  host_social_links: SocialLink[];
  timezone: string;
  duration_minutes: number;
  location: string | null;
  conferencing_provider: ConferenceProvider | null;
  respond_by: string | null;
  status: PollStatus;
  voting_open: boolean;
  confirmed_option_id: string | null;
  options: { id: string; start_at: string; end_at: string }[];
}

/** One row of a respondent's own ballot. The counts are absent unless `counts_visible`. */
export interface PollBallotOption {
  id: string;
  start_at: string;
  end_at: string;
  my_choice: PollVoteChoice | null;
  yes: number | null;
  less_preferred: number | null;
  no: number | null;
}

/** A respondent's own ballot, reached with their manage token: the receipt and the amend page. */
export interface PollBallot {
  title: string;
  description: string | null;
  host_name: string;
  /** The host's picture, or null when their plan does not include publishing it. */
  host_avatar_url: string | null;
  /** The host's header image, or null. */
  host_banner_url: string | null;
  /** The host's social profiles, in their chosen order. Free at every tier. */
  host_social_links: SocialLink[];
  timezone: string;
  duration_minutes: number;
  location: string | null;
  conferencing_provider: ConferenceProvider | null;
  respond_by: string | null;
  status: PollStatus;
  voting_open: boolean;
  confirmed_option_id: string | null;
  meeting_url: string | null;
  manage_token: string;
  name: string;
  email: string;
  note: string | null;
  /** Counts only, never names, and only once this person has voted (or voting is over). */
  counts_visible: boolean;
  options: PollBallotOption[];
}

/** Casting or amending a ballot. `website` is the honeypot: a real person never fills it. */
export interface PollVoteRequest {
  name: string;
  email: string;
  note?: string | null;
  website?: string;
  votes: { option_id: string; choice: PollVoteChoice }[];
}

/** What happened to a poll. CONFIG = the host's edits/lifecycle; VOTE = invitee activity. */
export type PollEventCategory = "CONFIG" | "VOTE";

export type PollEventType =
  | "VOTED"
  | "VOTE_AMENDED"
  | "POLL_CREATED"
  | "CONFIG_CHANGED"
  | "OPENED"
  | "CLOSED"
  | "CONFIRMED"
  | "UNCONFIRMED"
  | "CONFIRMATION_SENT"
  | "DELETED";

/** One entry in a poll's journal (GET /polls/{id}/events), newest first. */
export interface PollEvent {
  id: string;
  respondent_id: string | null;
  category: PollEventCategory;
  type: PollEventType;
  actor: "INVITEE" | "HOST" | "SYSTEM";
  actor_email: string | null;
  detail: string | null;
  occurred_at: string;
}

// ------------------------------------------------------- admission (A2A marketplace)

/**
 * The vocabulary a host writes admission rules in, served by the backend rather than restated
 * here. The set is closed on the server; a copy in this file would be a copy that can drift, and a
 * drifted copy fails quietly — offering a clause the server refuses, or hiding one it would take.
 */
export interface AdmissionVocabulary {
  predicates: AdmissionPredicateTerm[];
  outcomes: AdmissionOutcomeTerm[];
}

export interface AdmissionPredicateTerm {
  type: string;
  /** The sentence fragment, reading on from "If". */
  clause: string;
  value_source: "NONE" | "FREE_TEXT" | "PROVIDER";
  multiple: boolean;
  /** Whether a provider vouches for what it reads. Only these may carry an automatic booking. */
  verified: boolean;
  example: string | null;
  /** The closed set the blank is picked from, or empty when it is typed. */
  choices: { value: string; label: string }[];
}

export interface AdmissionOutcomeTerm {
  outcome: string;
  clause: string;
  /** Whether "up to N minutes" belongs after this outcome. */
  caps_duration: boolean;
}

export interface AdmissionPredicateView {
  type: string;
  value: string | null;
}

export interface AdmissionRuleView {
  id: string | null;
  ordinal: number;
  outcome: string;
  max_minutes: number | null;
  predicates: AdmissionPredicateView[];
}

export interface AdmissionPolicyView {
  pattern_id: string;
  pattern_name: string;
  rules: AdmissionRuleView[];
  vocabulary: AdmissionVocabulary;
}

/** The whole policy, not a delta. Rule order comes from list position. */
export interface AdmissionPolicyRequest {
  rules: {
    outcome: string;
    max_minutes: number | null;
    predicates: AdmissionPredicateView[];
  }[];
}

// ------------------------------------------------------- agent requests (A2A marketplace)

/**
 * One line of a host's inbox: somebody's agent asked them for time.
 *
 * <p>The caller is split by provenance rather than flattened, because the host is about to judge a
 * person and the difference matters more than the values do. `verified` is what a provider stood
 * behind; `stated` is what the person typed. They read identically on a screen and mean completely
 * different things, so they must not be rendered alike.</p>
 */
export interface AgentRequestView {
  id: string;
  received_at: string;
  caller: {
    provider: string;
    verified?: { email?: string; domain?: string; tenant?: string };
    stated?: { name?: string; email?: string };
  };
  purpose?: {
    stated?: string;
    classified?: string;
    /** False means the model worked it out rather than being told, which never auto-books. */
    stated_explicitly: boolean;
    /** How well it matched: NONE, LOW, MEDIUM or HIGH. Only HIGH ever books without asking. */
    confidence?: string;
  };
  outcome: string;
  /**
   * Why it was turned away, as a RefusalReason name. Absent on an admitted request, and on an old
   * row the V105 backfill could not place.
   *
   * The prose behind this is deliberately not served: it names pattern ids and rule ordinals, so
   * rendering it would recite the host's own policy back at them one ordinal at a time. Read it
   * through `refusalLabel` / `refusalNote`, which fall back to a generic line rather than throwing
   * on a code this build has not heard of.
   */
  reason_code?: string;
  pattern_id?: string;
  session_id?: string;
  /** What is still open, when anything is. Absent on a request that left nothing to do. */
  decision?: AgentRequestDecision;
}

export interface AgentRequestDecision {
  /**
   * `CONSIDER` is deliberately not `AWAITING_YOU`: nothing is held, nothing is booked, and the
   * person was told none of it and is not waiting on an answer (D-94).
   */
  state: "AWAITING_YOU" | "BOOKED" | "LAPSED" | "CLOSED" | "MOVING" | "CONSIDER";
  /** The hold to approve or decline. Absent once there is nothing to act on. */
  hold_id?: string;
  starts_at?: string;
  ends_at?: string;
  expires_at?: string;
  /** The one to act on when `state` is `CONSIDER`. Never a hold. */
  consideration_id?: string;
}

// ------------------------------------------------------- personal access tokens (MCP 3)

/**
 * One settings-list row for a personal access token. There is no secret field: the token value
 * exists in exactly one response, the one that created it (`PersonalTokenIssued`).
 */
export interface PersonalTokenView {
  id: string;
  name: string;
  /** Displayable head of the value ("scheda_pat_Ab12Cd34"), identifying but not sensitive. */
  prefix: string;
  scopes: string[];
  created_at: string;
  /** How a forgotten token gets noticed. Absent until first use. */
  last_used_at?: string;
  expires_at?: string;
  /** Set means revoked (kept for audit); the row stops working the moment it is set. */
  revoked_at?: string;
}

/** The mint response: the only place `secret` ever appears. */
export interface PersonalTokenIssued {
  token: PersonalTokenView;
  secret: string;
}
